import dagre from '@dagrejs/dagre';
import { ref } from '@vue/composition-api';

import { Edge, MarkerType, Node, Position } from '../types';
import {
  IWorkflowState,
  IWorkflowTransition,
  WorkflowStateTypeEnum
} from '@/models';
import { useWorkFlow } from './useWorkFlow';

export function useWorkFlowGraph() {
  function parseTransitionIntoEdge(
    transition: IWorkflowTransition
  ): Edge<IWorkflowTransition> {
    const sourceId = (transition.source_state as IWorkflowState).id!.toString();
    const targetId = (transition.target_state as IWorkflowState).id!.toString();

    return {
      id: transition.id!.toString(),
      type: sourceId === targetId ? 'loopback' : 'default',
      label: transition.name,
      /** Source node id */
      source: sourceId,
      /** Target node id */
      target: targetId,
      /** Disable/enable selecting edge */

      /** handle position */
      targetPosition: Position.Left,
      /** handle position */
      sourcePosition: Position.Right,

      selectable: true,

      updatable: true,
      /** Disable/enable deleting edge */
      deletable: true,
      /** Radius of mouse event triggers (to ease selecting edges), defaults to 2 */
      interactionWidth: 4,
      data: transition,

      markerEnd: {
        type: MarkerType.Arrow,
        strokeWidth: 2,
        width: 20,
        height: 20
      }
    } as Edge<IWorkflowTransition>;
  }

  function parseTransitionsIntoEdges(transitions: IWorkflowTransition[]) {
    return transitions.map(parseTransitionIntoEdge);
  }

  function getNodeTypeFromState(state: IWorkflowState) {
    if (state.default) return 'output';
    return 'default';
  }

  function parseStatesIntoNodes(states: IWorkflowState[]) {
    return states.map(
      state =>
        ({
          id: state.id!.toString(),
          label: state.name,
          position: { x: 0, y: 0 },
          type: getNodeTypeFromState(state),

          targetPosition: Position.Right,
          sourcePosition: Position.Left,

          draggable: true,
          selectable: true,
          connectable: true,
          // focusable: true
          /** Disable/enable deleting node */
          deletable: true,
          hidden: false,
          width: 200,
          height: 80,
          data: state
        } as Node<IWorkflowState>)
    );
  }

  return {
    getNodeTypeFromState,
    parseTransitionIntoEdge,
    parseTransitionsIntoEdges,
    parseStatesIntoNodes
  };
}

/**
 * Composable to run the layout algorithm on the graph.
 * It uses the `dagre` library to calculate the layout of the nodes and edges.
 */
export function useHorizontalLayout() {
  const { findNode } = useWorkFlow();

  const graph = ref(new dagre.graphlib.Graph());

  function layout(
    nodes: Node<IWorkflowState>[],
    edges: Edge<IWorkflowTransition>[]
  ): Node<IWorkflowState>[] {
    // we create a new graph instance, in case some nodes/edges were removed, otherwise dagre would act as if they were still there
    const dagreGraph = new dagre.graphlib.Graph();

    graph.value = dagreGraph;

    dagreGraph.setDefaultEdgeLabel(() => ({}));

    dagreGraph.setGraph({ rankdir: 'LR' });

    for (const node of nodes) {
      // if you need width+height of nodes for your layout, you can use the dimensions property of the internal node (`GraphNode` type)
      const graphNode = findNode(node.id);
      if (graphNode) {
        dagreGraph.setNode(node.id, {
          width: graphNode.dimensions.width || 145,
          height: graphNode.dimensions.height || 60
        });
      }
    }

    for (const edge of edges) {
      dagreGraph.setEdge(edge.source, edge.target, {
        label: edge.label,
        labeloffset: 0,
        labelpos: 'c',
        width: 200,
        height: 200
      });
    }

    dagre.layout(dagreGraph);

    // set nodes with updated positions
    return nodes.map(node => {
      const nodeWithPosition = dagreGraph.node(node.id);

      return {
        ...node,
        targetPosition: Position.Left,
        sourcePosition: Position.Right,
        position: { x: nodeWithPosition.x, y: nodeWithPosition.y }
      };
    });
  }

  return { graph, layout };
}
